import WebsiteRepositoryInterface, {
  Website,
  WebsiteComposite,
  WebsiteCreation,
  WebsiteDeletion,
  WebsiteOrchestratorResourceInterface,
  WebsiteOrchestratorResourcesInterface,
  WebsiteSearchResponseInterface,
  WebsiteToggle,
} from '@/services/Website/interfaces';
import { AxiosInstance } from 'axios';
import { OrchestratorActionStatus, OrchestratorActionType } from '@/services/WebsiteMigrations/interfaces';

export default class WebsiteRepository implements WebsiteRepositoryInterface {
  constructor(
    private readonly axios: AxiosInstance,
  ) {
  }

  public async getWebsiteById(id: number): Promise<WebsiteComposite> {
    const resp = await this.axios.get<WebsiteComposite>(`/websites/${id}`);

    return resp.data;
  }

  public async getWebsitesByIds(ids: number[], page?: number): Promise<Website[]> {
    if (!ids.length) {
      return [];
    }

    const resp = await this.axios
      .get<WebsiteSearchResponseInterface>(`websites?ids=${ids.join(',')}`);

    return resp.data.Websites;
  }

  public async searchWebsites(
    page?: number,
    domainSearch?: string,
    userId?: string,
    serverSearch?: string,
    statusSearch?: string,
    pageSize?: number,
  ): Promise<WebsiteSearchResponseInterface> {
    const query = new URLSearchParams();
    if (userId !== undefined && userId !== '') {
      query.set('userid', userId);
    }

    if (domainSearch !== undefined && domainSearch !== '') {
      query.set('domain', domainSearch);
    }

    if (serverSearch !== undefined && serverSearch !== '') {
      query.set('server', serverSearch);
    }

    if (statusSearch !== undefined && statusSearch !== '') {
      query.set('status', statusSearch);
    }

    if (page !== undefined) {
      query.set('page', String(page));
    }

    if (pageSize !== undefined) {
      query.set('pageSize', String(pageSize));
    }

    const resp = await this.axios
      .get<WebsiteSearchResponseInterface>(`websites?${query.toString()}`);

    return resp.data;
  }

  public async getByServerAndDomain(server: string, domain?: string, page?: number): Promise<Website[]> {
    const query = new URLSearchParams();
    query.set('server', server);

    if (domain !== undefined) {
      query.set('domain', domain);
    }

    if (page !== undefined) {
      query.set('page', String(page));
    }

    const resp = await this.axios.get<WebsiteSearchResponseInterface>(`websites?${query.toString()}`);

    return resp.data.Websites;
  }

  public async getWebsiteCreations(id: number): Promise<WebsiteCreation[]> {
    const resp = await this.axios.get<WebsiteCreation[]>(`/websites/${id}/creations`);

    return resp.data;
  }

  public async getWebsiteDeletions(id: number): Promise<WebsiteDeletion[]> {
    const resp = await this.axios.get<WebsiteDeletion[]>(`/websites/${id}/deletions`);

    return resp.data;
  }

  public async createWebsite(
    originServer: string,
    domain: string,
    userId: string,
    groupId: number | null | undefined,
    excludeActions: string[],
    plan: number,
  ): Promise<Website> {
    const request: any = {
      originServer,
      domain,
      userId,
      excludeActions,
      plan,
    };

    if (groupId !== undefined) {
      request['groupId'] = groupId;
    }

    const response = await this.axios.post(
      '/websites',
      request,
    );

    return response.data;
  }

  public async createSubwebsite(websiteId: number, domain: string, userId: string, websiteEnvironment: string, excludeActions: string[]): Promise<Website> {
    const response = await this.axios.post(
      `/websites/${websiteId}/subwebsite`,
      {
        domain,
        websiteId,
        websiteEnvironment,
        userId,
        excludeActions,
      },
    );

    return response.data;
  }

  public async createWebsiteStaging(websiteId: number, websiteEnvironment: string, userId: string): Promise<Website> {
    const response = await this.axios.post(
      `/websites/${websiteId}/staging`,
      {
        websiteId,
        websiteEnvironment,
        userId,
      },
    );

    return response.data;
  }

  public async createWebsiteAlias(
    websiteId: number,
    domain: string,
    excludeActions: string[],
    websiteEnvironment: string,
    userId: string,
  ): Promise<Website> {
    const response = await this.axios.post(
      `/websites/${websiteId}/alias`,
      {
        websiteId,
        domain,
        excludeActions,
        websiteEnvironment,
        userId,
      },
    );

    return response.data;
  }

  public async deleteWebsite(websiteId: number): Promise<void> {
    await this.axios.delete(`/websites/${websiteId}`);
  }

  public async toggleWebsite(websiteId: number, status: string): Promise<void> {
    await this.axios.patch(`/websites/${websiteId}`, { status });
  }

  public async getWebsiteToggles(id: number): Promise<WebsiteToggle[]> {
    const response = await this.axios.get(`/websites/${id}/toggles`);

    return response.data;
  }

  public async recreateWebsite(id: number): Promise<void> {
    await this.axios.patch(`/websites/${id}/retry`);
  }

  public async retryAction(websiteId: number, creationId: number, actionId: number): Promise<void> {
    await this.axios.patch(`/websites/${websiteId}/retry/${creationId}/${actionId}`);
  }

  public async getWebsiteOrchestratorResources(websiteId: number): Promise<WebsiteOrchestratorResourcesInterface> {
    const response = await this.axios.get(`/websites/${websiteId}/orchestrator`);

    return response.data;
  }

  public async getWebsiteOrchestratorResource(
    websiteId: number,
    resourceId: number,
    resourceType: number,
  ): Promise<WebsiteOrchestratorResourceInterface> {
    const response = await this.axios.get(
      `/websites/${websiteId}/orchestrator/${resourceType}/${resourceId}`,
    );

    return response.data;
  }

  public async updateActionStatus(
    websiteId: number,
    actionId: number,
    actionParentId: number,
    actionType: OrchestratorActionType,
    status: OrchestratorActionStatus,
  ): Promise<void> {
    await this.axios.patch(
      `/websites/${websiteId}/orchestrator/${actionParentId}/actions/${actionId}`,
      {
        actionType,
        status,
      },
    );
  }
}
